/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import { Card, Col, Form, Input, Row, Select } from 'antd';

// Misc imports
import is from '../../../utils/is';
import i18n from '../../../i18n';

// Constants

export const AGENT_MASKING_SETTINGS_CUSTOM_KEY = 'customMaskingMode';

export const AGENT_MASKING_SETTINGS_MODES = [
  {
    title: i18n.t('Full Name'),
    key: 'fullName',
    description: i18n.t('Show full real name'),
    displayExample: i18n.t('John Joe Doe'),
    agentNameRegex: null,
    agentNameSubstitution: null,
    showRegex: false,
    showOutput: false,
    isRegexEditable: false,
    isOutputEditable: false
  },
  {
    title: i18n.t('First Name + Initial from Last Name'),
    key: 'firstNameInitial',
    description: i18n.t('Partial privacy'),
    displayExample: i18n.t('John D.'),
    agentNameRegex: '(^\\w+)\\s(.+\\s)*(\\w)\\w+$',
    agentNameSubstitution: '$1 $3.',
    showRegex: false,
    showOutput: false,
    isRegexEditable: false,
    isOutputEditable: false
  },
  {
    title: i18n.t('First Name Only'),
    key: 'firstNameOnly',
    description: i18n.t('Friendly'),
    displayExample: i18n.t('John'),
    agentNameRegex: '(^\\w+).+',
    agentNameSubstitution: '$1',
    showRegex: false,
    showOutput: false,
    isRegexEditable: false,
    isOutputEditable: false
  },
  {
    title: i18n.t('Constant Name'),
    key: 'constant',
    description: i18n.t('Full anonymity'),
    displayExample: i18n.t('Agent', { context: 'livechat agent' }),
    agentNameRegex: '.+',
    agentNameSubstitution: 'Agent',
    showRegex: false,
    showOutput: true,
    isRegexEditable: false,
    isOutputEditable: true
  },
  {
    title: i18n.t('Advanced Masking'),
    key: AGENT_MASKING_SETTINGS_CUSTOM_KEY,
    description: i18n.t('Customized'),
    displayExample: null,
    agentNameRegex: '',
    agentNameSubstitution: '',
    showRegex: true,
    showOutput: true,
    isRegexEditable: true,
    isOutputEditable: true
  }
];

/**
 * @typedef {object} AgentMaskingProps
 * @property {object} form - The antd form instance from the parent component.
 */

/**
 * =====================================================================================================
 *
 * CODE DESCRIPTION
 * This component renders the form fields for the Agent Masking feature.
 * It includes a dropdown to select the mode, which automatically populates
 * the RegEx and Output Format fields. These fields are only editable
 * when 'Custom Masking' or 'Constant Name' is selected.
 *
 * =====================================================================================================
 */

const AgentMasking = ({
  form
}) => {

  const currentModeKey = Form.useWatch('agentMaskingMode', form);

  const currentModeConfig = useMemo(() => {
    return AGENT_MASKING_SETTINGS_MODES.find(m => m.key === currentModeKey) || AGENT_MASKING_SETTINGS_MODES[0];
  }, [currentModeKey]);

  return (
    <Row>
      <Col xs={24}>
        {/* This field is only local and is not being sent to the backend,
       it's used to identify in the client which mode we are currently in */}
        <Form.Item
          className="mb-2"
          name={'agentMaskingMode'}
          label={t('Mode')}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Select
            placeholder={t('Select a masking mode')}
            size="large"
            getPopupContainer={triggerNode => triggerNode.parentNode}
            showSearch
            onSelect={(item) => {
              const currentMaskingItem = AGENT_MASKING_SETTINGS_MODES.find(maskingSetting => maskingSetting.key === item);

              form.setFieldsValue({ agentNameRegex: currentMaskingItem.agentNameRegex, agentNameSubstitution: currentMaskingItem.agentNameSubstitution });
            }}
            defaultValue={AGENT_MASKING_SETTINGS_MODES[0].key}
            filterOption={(input, option) =>
              (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                  || (option?.description ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={AGENT_MASKING_SETTINGS_MODES.map((item) => (
              {
                label: <>
                  {item.title}
                  <span className="text-secondary fs-sm">&nbsp;-&nbsp;{item.description}{item?.displayExample && `: ${item.displayExample}`}</span>
                </>,
                value: item.key,
                description: item.description
              }
            ))}
          />
        </Form.Item>
      </Col>
      <Col xs={24}>
        <Card
          hidden={!currentModeConfig.showOutput && !currentModeConfig.showRegex}
          className={`bg-layout-gray mb-3 shadow-none border-0`}
        >
          <Form.Item
            label={t('Matching Rule (RegEx)')}
            name="agentNameRegex"
            hidden={!currentModeConfig.showRegex}
            dependencies={['agentMaskingMode']}
            rules={[{
              required: currentModeConfig?.isRegexEditable,
              message: t('Regex pattern is required')
            }, {
              validator: async (rule, value) => {

                if (!currentModeConfig.isRegexEditable) {
                  return Promise.resolve();
                }

                const isValid = is.validRegex(value);
                if (!isValid) {
                  return Promise.reject();
                }

                return Promise.resolve();
              },
              message: t('Please enter a valid Regex pattern')
            }]}
            normalize={(value) => {
              // Remove surrounding forward slashes and unescape backslashes
              if (value) {
                return value.replace(/^\/|\/$/g, '');
              }
              return value;
            }}
          >
            <Input
              className={`${!currentModeConfig?.isRegexEditable ? 'bg-layout-gray' : ''}`}
              readOnly={!currentModeConfig?.isRegexEditable}
            />
          </Form.Item>
          <Form.Item
            label={currentModeConfig.key === AGENT_MASKING_SETTINGS_CUSTOM_KEY ? t('Output Format') : t('Name')}
            dependencies={['agentMaskingMode']}
            name="agentNameSubstitution"
            hidden={!currentModeConfig.showOutput}
            rules={[{
              required: currentModeConfig?.isOutputEditable,
              message: currentModeConfig.key === AGENT_MASKING_SETTINGS_CUSTOM_KEY ? t('Output format is required') : t('Name is required')
            }]}
          >
            <Input
              className={`${!currentModeConfig?.isOutputEditable ? 'bg-layout-gray' : ''}`}
              readOnly={!currentModeConfig?.isOutputEditable}
            />
          </Form.Item>
        </Card>
      </Col>
    </Row>
  );
};

AgentMasking.propTypes = {
  form: PropTypes.object.isRequired
};

export default AgentMasking;
